import React, { useRef, useMemo, useState } from "react";
import styled from "styled-components";
import { useDimensions } from "common/hooks";
import * as d3 from "d3";
import colors from "common/graphs/colors";
import colorsDark from "common/graphs/colorsDark";

const CHART_HEIGHT = 450;
const CHART_MARGIN = { top: 30, right: 30, bottom: 50, left: 200 };
const PADDING_Y_AXIS = 10;

const ChartContainer = styled.div`
  position: relative;
  width: 100%;
  overflow: visible;
`;

const WrapperChart = styled.div`
  width: 100%;
  height: 100%;
  position: relative;

  svg {
    width: 100%;
    height: 100%;

    .clickable {
      cursor: pointer;
    }

    .multiBarChartAxis {
      fill: var(--chart-axis-text-color);
      color: #929295;
      font-family: var(--chart-font-family, Arial, sans-serif);
      font-size: 11px !important;

      text {
        font-size: 11px !important;
      }
      &.yAxis {
      line {
      stroke-opacity: 0.4;
      }
      }
      &.xAxis {
        text {
          font-size: 11px !important;
          &.headLabel {
          font-size: 10px !important;
          }
        }
      }
    }
    .service {
      background: pink;
    }
  }
`;

const MIN_HEIGHT_CELL = 25;
const MAX_HEIGHT_CELL = 150;
const MIN_HEIGHT_CHART = 500;

const getHeight = (fieldsNr) => {
  const minHeightCnt = Math.floor(MIN_HEIGHT_CHART / MAX_HEIGHT_CELL);
  const maxHeightCnt = Math.floor(MIN_HEIGHT_CHART / MIN_HEIGHT_CELL);

  if (fieldsNr > 0 && fieldsNr <= minHeightCnt) {
    return fieldsNr * MAX_HEIGHT_CELL;
  } else if (fieldsNr > minHeightCnt && fieldsNr <= maxHeightCnt) {
    return MIN_HEIGHT_CHART;
  } else {
    return fieldsNr * MIN_HEIGHT_CELL;
  }
};

function MultiBarChart({ data, categories, onSelect, overlap, meanLabel, yAxisLabel, yAxisUnit }) {
  const chartRef = useRef(null);
  const dimensions = useDimensions(chartRef);
  const { width } = dimensions;
  const chartHeight = getHeight(data.length);
  const overlapHeight = data.length > 0 ? (chartHeight / data.length) * (overlap - 1) : 0;
  const height = chartHeight + CHART_MARGIN.top + CHART_MARGIN.bottom + PADDING_Y_AXIS + overlapHeight;
  const margin = {...CHART_MARGIN, top: CHART_MARGIN.top + overlapHeight};
  const widthChart = width - margin.left - margin.right;
  const heightChart = height - margin.top - margin.bottom;
  const colorsSet = login.isTheme("light") ? colors : colorsDark;
  const [hoveredCell, setHoveredCell] = useState(null);

  const allSeries = data.map((series) => series.name);

  const seriesScale = d3
    .scaleBand()
    .domain(allSeries)
    .range([0, heightChart - PADDING_Y_AXIS]);

  const heightBars = seriesScale.bandwidth() * overlap;
  const yScale = d3
    .scaleLinear()
    .domain([0, 100])
    .range([0, heightBars - PADDING_Y_AXIS / data.length]);

  const yLabels = useMemo(() => {
    return data.map((series, i) => {
      return (
        <g key={series.name}>
          <line
            x1={widthChart}
            x2={0}
            y1={seriesScale(series.name) + seriesScale.bandwidth()}
            y2={seriesScale(series.name) + seriesScale.bandwidth()}
            stroke="var(--chart-axis-text-color)"
          />
          <g onClick = {() => onSelect(series.name)} className={onSelect ? "clickable" : ""}>
          <text
            key={`y-label-${i}`}
            x={-90}
            y={seriesScale(series.name) + seriesScale.bandwidth()}
            textAnchor="end"
            dominantBaseline="middle"

          >
            {series.name}
          </text>
          <circle
          cx={-80}
          cy={seriesScale(series.name) + seriesScale.bandwidth() -2}
          r="4"
          stroke={colorsSet[i]}
          fill={
            colorsSet[i]
          }
        />
        </g>
         <text
            key={i}
            x={-10}
            y={seriesScale(series.name) + seriesScale.bandwidth()}
            textAnchor="end"
            dominantBaseline="middle"
          >
            {d3.format(".2f")(series.meanValue)}
          </text>
        </g>
      );
    });
  }, [JSON.stringify(data), widthChart]);

  const xScale = useMemo(() => {
    return d3
      .scaleBand()
      .domain(categories)
      .range([0, widthChart])
      .padding(0.05);
  }, [categories.join(","), width]);

  const xLabels = useMemo(() => {
    return categories.map((category, i) => {
      return (
        <g
          key={category}
          transform={`translate(${xScale(category) + xScale.bandwidth() / 2},${
            heightChart + 20
          })`}
        >
          <text key={i} x={0} y={0} textAnchor="begin" transform="rotate(30)">
            {category}
          </text>
          <line
            x1={0}
            x2={0}
            y1={-15}
            y2={-5}
            stroke="var(--chart-axis-text-color)"
          />
        </g>
      );
    });
  }, [categories.join(","), width]);

  const bars = data.map((d, i) => {
    const { name, values } = d;
    const yService = seriesScale(name);
    if (yService === undefined) {
      return null;
    }
    return (
      <g
        className={`service ${name}`}
        key={`${name}`}
        transform={`translate(${[0, yService].join(",")})`}
      >
        {values.map((item) => {
          const y = yScale(item.value);
          if (y === undefined) {
            return null;
          }
          return (
            <g key={`${name}-${item.name}`}>
              <rect
                x={xScale(item.name)}
                y={seriesScale.bandwidth() - yScale(item.value)}
                height={yScale(item.value)}
                width={xScale.bandwidth() * 0.8}
                opacity={0.4}
                fill={colorsSet[i]}
                rx={1}
                onMouseEnter={(e) => {
                  if (overlap < 1){
                    setHoveredCell({ category: item.name, value:item.value, xPos: xScale(item.name) + margin.left, yPos: yService + seriesScale.bandwidth()- yScale(item.value) });
                  }
                }}
                onMouseLeave={() => {
                  setHoveredCell(null);
                }}
              />
            </g>
          );
        })}
      </g>
    );
  });

  return (
    <ChartContainer>
      <WrapperChart ref={chartRef} style={{ height }}>
        <svg>
          <g 
            width={width}
            height={height}
            transform={`translate(${[margin.left,0].join(",")})`}
            className="multiBarChartAxis xAxis"
            >
              <>
              <text
              className="headLabel"
              key="mean-label"
              x={-5}
              y={PADDING_Y_AXIS + 10}
                    textAnchor="end"
            dominantBaseline="middle"
              >{meanLabel}</text>
              <text
              key="app-label"
              className="headLabel"
              x={-75}
              y={PADDING_Y_AXIS + 10}
                    textAnchor="end"
            dominantBaseline="middle"
              >{yAxisLabel}</text>
              </>
            </g>
          <g
            width={width}
            height={height}
            transform={`translate(${[margin.left, margin.top].join(",")})`}
          >
            <g className="multiBarChartAxis yAxis">
                {yLabels}
            </g>
            <g className="multiBarChartAxis xAxis">
              <>{xLabels}</>
              <line x1={0} x2={widthChart} y1={heightChart} y2={heightChart} stroke="var(--chart-inner-border-color)"/>
              </g>
            <g>{bars}</g>
            
          </g>
        </svg>
      </WrapperChart>
      <Tooltip interactionData={hoveredCell} unit={yAxisUnit} />
    </ChartContainer>
  );
}

export default MultiBarChart;

const TooltipWrapper = styled.div`
  position: absolute;
  background-color: rgba(0, 0, 0, 0.8);
  border: 1px solid rgba(0, 0, 0, 0.5);
  border-radius: 4px;
  color: white;
  font-size: 12px;
  padding: 6px;
  transform: translate(-50%, -50%);
  font-family: "AllianceNo2-Regular" !important;
  z-index: 200;
`;

const TooltipValues = styled.div`
  margin-top: 10px;
  display: flex;
  align-items: center;
`;

const TooltipLabel = styled.div`
  margin: 0 0px;
  width: 60px;
`;

const TooltipValue = styled.div`
margin-left: 10px;
width: 45px;
  font-family: "AllianceNo2-SemiBold" !important;
`;

const Tooltip = ({ unit, interactionData }) => {
  if (!interactionData) {
    return null;
  }

  const { category, xPos, yPos, value } = interactionData;

  return (
    <div
      style={{
        height: "auto",
        width: "auto",
        position: "absolute",
        top: -10,
        left: 30,
        pointerEvents: "none",
      }}
    >
      <TooltipWrapper
        style={{
          position: "absolute",
          width: "auto",
          left: xPos -80,
          top: yPos - 20,
        }}
      >
        <TooltipValues>
        <TooltipLabel>{category}:</TooltipLabel>
        <TooltipValue>{`${d3.format(".1f")(value)} ${unit}`}</TooltipValue>
        </TooltipValues>
      </TooltipWrapper>
    </div>
  );
};